import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AlertTriangle, IndianRupee, ChevronRight, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dashboard tile that surfaces the split count of Offered + Joined
 * applications missing `offered_ctc`. Click-through routes to the
 * CtcCleanup page's new "Missing CTC" section for inline backfill.
 *
 * Auto-hides when both counts are zero so the dashboard stays clean
 * for orgs with healthy CTC hygiene.
 *
 * Visibility: admin-only (matches the `/admin/ctc-cleanup` route gating).
 * Other internal roles see the older `MissingCtcBanner` for Joined-only
 * backfill but don't get the click-through here.
 */
const MissingCtcStageTile = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [data, setData] = useState({ offered_count: 0, joined_count: 0, total: 0 });
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setLoading(true);
    const load = () => axios
      .get(`${API}/admin/ctc-missing`, { params: { limit: 1 } })
      .then((res) => {
        if (cancelled) return;
        setData({
          offered_count: res.data?.offered_count || 0,
          joined_count: res.data?.joined_count || 0,
          total: res.data?.total || 0,
        });
      })
      .catch(() => {
        if (!cancelled) setData({ offered_count: 0, joined_count: 0, total: 0 });
      })
      .finally(() => {
        if (!cancelled) {
          setLoaded(true);
          setLoading(false);
        }
      });
    load();
    // Refresh when somebody backfills a CTC elsewhere on the dashboard
    const onBackfill = () => load();
    window.addEventListener('ctc:backfilled', onBackfill);
    return () => {
      cancelled = true;
      window.removeEventListener('ctc:backfilled', onBackfill);
    };
  }, [isAdmin]);

  if (!isAdmin || !loaded) return null;
  if (data.total === 0) return null;

  return (
    <Link
      to="/admin/ctc-cleanup"
      data-testid="missing-ctc-stage-tile"
      className="block rounded-lg border border-amber-200 bg-white hover:border-amber-300 hover:shadow-sm transition p-3 group"
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-md bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0">
          {loading ? (
            <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />
          ) : (
            <AlertTriangle className="w-4 h-4 text-amber-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
            Needs CTC
          </div>
          <div className="text-sm text-slate-900 mt-0.5 flex items-baseline gap-2 flex-wrap">
            <span className="font-mono font-bold text-amber-700" data-testid="missing-ctc-stage-offered">
              {data.offered_count}
            </span>
            <span className="text-[11px] text-slate-500">offered</span>
            <span className="text-slate-300">·</span>
            <span className="font-mono font-bold text-amber-700" data-testid="missing-ctc-stage-joined">
              {data.joined_count}
            </span>
            <span className="text-[11px] text-slate-500">joined</span>
          </div>
        </div>
        <div className="flex items-center gap-1 text-[11px] font-semibold text-amber-700 opacity-70 group-hover:opacity-100 transition">
          <IndianRupee className="w-3 h-3" />
          Fix
          <ChevronRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </Link>
  );
};

export default MissingCtcStageTile;
